export type AppUser = {
  id: string
  email: string
  full_name: string | null
  role: 'owner' | 'admin'
  auth_user_id: string | null
  is_active: boolean
  created_at: string
  last_seen_at: string | null
}

export type Agent = {
  rezen_id: string
  first_name: string | null
  last_name: string | null
  email: string | null
  join_real_email: string | null
  phone: string | null
  license_number: string | null
  license_expires: string | null
  state: string | null
  team_name: string | null
  team_role: string | null
  is_team_leader: boolean

  // Recurrence fields. These are what the annual triggers fire on.
  birthday_month: number | null
  birthday_day: number | null
  anniversary_month: number | null
  anniversary_day: number | null

  // Full dates, populated only where the year is known. `anniversary` and
  // `anniversary_year` exist to compute "N years with Real" — they are NOT the
  // recurrence field, and there is deliberately no `birthday` equivalent
  // because birthdays never carry a year.
  anniversary: string | null
  anniversary_year: number | null

  date_became_capped: string | null
  date_became_elite: string | null
  cap_reset_date: string | null
  first_transaction_closed_at: string | null

  pending_transactions: number | null
  closed_transactions: number | null
  active_listings: number | null
  network_size: number | null

  profile_link: string | null
  home_address: string | null
  mailing_address: string | null
  office_address: string | null
  primary_market: string | null
  service_area: string | null
  bio: string | null
  headshot_path: string | null

  mls: Record<string, unknown> | null
  board: Record<string, unknown> | null
  social_links: Record<string, unknown> | null

  region: string | null
  region_source: string | null
  bolt_profile_inaccessible: boolean

  is_active: boolean
  first_seen_at: string
  last_seen_at: string | null
  departed_at: string | null
}

export type AgentNote = {
  agent_id: string
  body: string
  updated_by: string | null
  updated_at: string
}

export type AgentTag = {
  agent_id: string
  tag: string
  created_by: string | null
  created_at: string
}

/**
 * The dedupe ledger. `occurrence` identifies WHICH instance of a trigger the
 * row is about (this year's birthday), not when it was processed — that is
 * `ts`. There is deliberately no created_at/sent_at pair here: one timestamp,
 * because the row records that an event was handled, not a delivery.
 */
export type OutreachLogRow = {
  id: number
  agent_id: string
  trigger: string
  occurrence: string
  status: 'baseline' | 'drafted' | 'sent' | 'skipped'
  ts: string
}

export type OutreachDraft = {
  id: number
  agent_id: string
  trigger: string
  occurrence: string
  subject: string
  body: string
  /** Why this draft exists — shown so a reviewer never has to guess. */
  why: string | null
  label: string | null
  days_elapsed: number | null
  facts: Record<string, unknown>
  status: 'pending' | 'sent' | 'skipped' | 'failed'
  /**
   * Set only when status is 'failed'. Was on the table (`outreach_drafts.error`)
   * since the very first migration but never carried on this type until the
   * agent-record Emails section (2026-08-22) needed to show WHY a failed
   * send failed, not just that it did.
   */
  error: string | null
  created_at: string
  sent_at: string | null
}

export type EmailTemplate = {
  key: string
  name: string
  subject: string
  body: string
  notes: string | null
  updated_by: string | null
  updated_at: string
}

export type SyncRun = {
  id: number
  kind: 'daily_metabase' | 'weekly_rezen'
  run_date: string
  result: 'ok' | 'failed' | 'aborted' | 'running'
  reason: string | null
  message: string | null
  stats: Record<string, unknown>
  started_at: string
  finished_at: string | null
}

/**
 * Templates whose key starts with an underscore are guidance for whoever writes
 * the emails — voice notes, belated variants, early variants. They are stored
 * in the same table because Renee edits them constantly and they must not live
 * in deployed code. They are NOT sendable templates and must never be handed to
 * a send path.
 */
export const isGuidanceTemplate = (key: string) => key.startsWith('_')

/**
 * A per-agent checklist item. Always tied to an agent — there is no
 * standalone task (`tasks.agent_id` is `not null`) — so this type carries no
 * optional agent reference the way a general-purpose to-do item might.
 */
export type Task = {
  id: number
  agent_id: string
  body: string
  due_date: string | null
  done: boolean
  /** Set when `done` flips true, cleared if it flips back. */
  done_at: string | null
  created_by: string | null
  created_at: string
}

/**
 * One row of `milestone_settings` — the Triggers tab's on/off switch per
 * milestone trigger (claude/milestone-triggers-tab-flagged-2026-08-23.md).
 * `trigger` is typed as `string`, not `Trigger`, on purpose: this is what
 * came back off the wire, and a stale or hand-edited row carrying a key
 * `milestones.ts` no longer has must not fail to *parse*, only fail to
 * *match* anything in `Trigger` when the tab renders it — same reasoning
 * `lib/milestones.ts` gives for keeping `TRIGGER_TAG`'s values as plain
 * strings.
 */
export type MilestoneSetting = {
  trigger: string
  enabled: boolean
  updated_at: string
  updated_by: string | null
}

/**
 * One bounce or spam complaint. `source: 'resend'` rows are written by
 * `functions/api/resend-webhook.ts` off a real Resend delivery event, keyed
 * to the Bulk-lane send via `campaign_send_id`. `source: 'manual'` rows are
 * a person reporting one seen in the Reviewed/Gmail lane, which this app
 * has no API visibility into (`gmail-lane.md`) — those key to the specific
 * Sent draft via `draft_id` when reported from that row, or carry neither
 * id for a general "her whole domain is down" report.
 */
export type Bounce = {
  id: number
  agent_id: string
  address: string
  subject: string | null
  kind: 'bounced' | 'complained'
  source: 'resend' | 'manual'
  campaign_send_id: number | null
  draft_id: number | null
  reason: string | null
  reported_by: string | null
  detected_at: string
}
